import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Heading from "components/Typography/Header";
import Button from "components/Button";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import swal from 'sweetalert';
import { createPayouts, reset, getLimitUsage } from "redux/reseller/payout/payoutSlice";
import { profile } from "redux/reseller/profile/profileSlice";
import { useUUID } from "hooks/useUUID";
import { useCustomSettings } from 'hooks/useCustomSettings';

const CreatePayoutsContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [trigger, setTrigger] = useState(0);
  const [lastFormValues, setLastFormValues] = useState(null);
  const uuid = useUUID(trigger);
  const {
    isCreatePayoutSuccess,
    isCreatePayoutError,
    isCreatePayoutLoading,
    limitUsage,
    isLimitUsageLoading,
    message
  } = useSelector((state) => state.resellerPayouts);

  const { profile: profileData } = useSelector(state => state.resellerProfile)
  
  // Get settings data for commission calculations
  const { data: settingsData } = useSelector(state => state.resellerSettings);
  const keyTextMapping = {}; // Assuming this might be needed for the hook
  const payoutFeeFixed = useCustomSettings(settingsData, keyTextMapping, 'payout_fee_fixed');

  // Format monetary values with Turkish locale
  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format limit usage display
  const formatLimitUsage = () => {
    if (!limitUsage || !profileData?.daily_autopayout_limit || profileData.daily_autopayout_limit <= 0) {
      return null;
    }

    const dailyLimit = parseFloat(profileData.daily_autopayout_limit);
    const usedAmount = parseFloat(limitUsage.used_amount || 0);
    const remainingAmount = parseFloat(limitUsage.remaining_amount || 0);

    return {
      dailyLimit: Math.floor(dailyLimit),
      usedAmount: Math.floor(usedAmount),
      remainingAmount: Math.floor(remainingAmount)
    };
  };

  // Check if amount is suitable for autopayout
  const isAutoPayoutSuitable = (amount) => {
    if (!amount || !limitUsage || !profileData?.daily_autopayout_limit || profileData.daily_autopayout_limit <= 0) {
      return false;
    }
    
    const netAmount = parseFloat(amount);
    const remainingAmount = parseFloat(limitUsage.remaining_amount || 0);
    
    return netAmount <= remainingAmount;
  };

  // Calculate commission based on backend logic - user enters net amount
  const calculateCommission = useMemo(() => {
    return (netAmount) => {
      if (!netAmount || !profileData?.payout_fee) {
        return { feeAmount: 0, grossAmount: netAmount || 0, netAmount: netAmount || 0 };
      }

      const netPayoutAmount = parseFloat(netAmount);
      const commissionRate = parseFloat(profileData.payout_fee);
      const fixedFee = parseFloat(payoutFeeFixed.value) || 0;
      
      // Backend formula: fee_amount = net_amount * fee_pct / (1 - fee_pct) + fixed_fee
      // gross_amount = net_amount + fee_amount
      const commissionRateDecimal = commissionRate / 100; // Convert percentage to decimal
      const feeAmount = netPayoutAmount * commissionRateDecimal / (1 - commissionRateDecimal) + fixedFee;
      const grossAmount = netPayoutAmount + feeAmount;

      return {
        feeAmount: parseFloat(feeAmount.toFixed(2)),
        grossAmount: parseFloat(grossAmount.toFixed(2)),
        netAmount: parseFloat(netPayoutAmount.toFixed(2))
      };
    };
  }, [profileData?.payout_fee, payoutFeeFixed]);

  const createPayoutForm = Yup.object().shape({
    iban: Yup.string()
      .required("IBAN zorunludur")
      .test('iban', 'IBAN TR ile başlamalı ve 26 karakter olmalı', function(value) {
        if (!value) return false;
        const cleanValue = value.replace(/\s/g, '');
        // Check if it's a Turkish IBAN (starts with TR and 26 chars total)
        if (cleanValue.toUpperCase().startsWith('TR')) {
          return cleanValue.length === 26;
        }
        return false;
      }),
    holder_name: Yup.string()
      .required("Hesap Sahibi Zorunludur")
      .test('no-digits', 'Hesap sahibi adında rakam bulunamaz', function(value) {
        if (!value) return false;
        return !/\d/.test(value);
      }),
    tckn: Yup.string()
      .test('tckn-format', 'TCKN 11 haneli, VKN 10 haneli olmalıdır', function(value) {
        if (!value || value.trim() === '') return true; // Allow empty
        const cleanValue = value.replace(/\s/g, ''); // Remove spaces
        return /^\d{10}$/.test(cleanValue) || /^\d{11}$/.test(cleanValue); // 10 or 11 digits
      }),
    net_amount_try: Yup.number().required("Tutar Zorunludur"),
  });

  const [resetFormFunc, setResetFormFunc] = useState({});

  useEffect(() => {
    dispatch(profile());
    dispatch(getLimitUsage());
  }, [dispatch]);

  useEffect(() => {
    if (isCreatePayoutLoading) {
      // Loading state
    } else if (isCreatePayoutError) {
      if(message && !message.toString().includes("undefined"))
        setTrigger((prev) => prev + 1);

      if (message.net_amount_try) {
        toast.error(message.net_amount_try);
      } else if (message.iban) {
        toast.error(message.iban);
      } else
        toast.error(message);
      
    } else if (message && message.need_confirm) {
      setTrigger((prev) => prev + 1);
      const el = document.createElement("div");
      el.innerHTML = message.message;
      swal({
        title: "Çekim Onayı",
        content: {
          element: el
        },
        icon: "info",
        buttons: ["İptal", "Onayla"]
      })
      .then(confirmed => {
        if (confirmed) {
          dispatch(
            createPayouts({
              iban: lastFormValues.iban,
              holder_name: lastFormValues.holder_name,
              net_amount_try: lastFormValues.net_amount_try.toString(),
              tckn: lastFormValues.tckn,
              nonce: uuid+"-confirm",
              confirm: true
            })
          );
        }
      });
    } else if (isCreatePayoutSuccess) {
      setTrigger((prev) => prev + 1);
      toast.success(message?.message ?? "Talebiniz Alınmıştır.");
      if (resetFormFunc.resetForm) {
        resetFormFunc.resetForm();
      }
      //navigate("/reseller/payouts");
    }
    return () => {
      dispatch(reset());
    };
  }, [isCreatePayoutError, isCreatePayoutSuccess, isCreatePayoutLoading, message, dispatch, lastFormValues, uuid]);

  const handleSubmit = async (values, {resetForm}) => {
    setResetFormFunc({resetForm: resetForm});
    setLastFormValues(values);
    dispatch(
      createPayouts({
        iban: values.iban,
        holder_name: values.holder_name,
        net_amount_try: values.net_amount_try.toString(),
        tckn: values.tckn,
        nonce: uuid,
        confirm: false
      })
    );
  };


  return (
    <div className="p-4 ">
      <Heading className="text-white-100" level={5}>
        Para Çekme Talebi
      </Heading>
      <div className="p-4 bg-gray-900 my-4 rounded-xl min-h-screen">
        <div className="grid grid-cols-1  gap-4">
          <div className="p-4 bg-black md:h-screen">
            <Formik
              initialValues={{
                iban: "",
                holder_name: "",
                tckn: "",
                net_amount_try: "",
              }}
              validationSchema={createPayoutForm}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue, values }) => {
                const commissionInfo = calculateCommission(values.net_amount_try);
                const currentBalance = parseFloat(profileData?.balance_try || 0);
                const finalBalance = currentBalance - commissionInfo.grossAmount;
                
                return (
                  <Form className="space-y-4 max-w-[350px]">
                    <div className="w-full">
                      <div className="text-white-100 font-medium mb-4">Hesabınızda Bulunan Bakiye: {formatMoney(profileData?.balance_try)} TL</div>
                      
                      <span className="text-white-100 font-medium">IBAN</span>
                      <Field
                        name="iban"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder="IBAN"
                      />
                      <ErrorMessage
                        name="iban"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">
                        Hesap Sahibi Ad Soyad
                      </span>
                      <Field
                        name="holder_name"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                      />
                      <ErrorMessage
                        name="holder_name"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">
                        TCKN/VKN
                      </span>
                      <Field
                        name="tckn"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                      />
                      <ErrorMessage
                        name="tckn"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">Net Alacağınız Tutar (TL)</span>
                      <Field
                        name="net_amount_try"
                        type="number"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder="Almak istediğiniz net tutarı giriniz"
                      />
                      <ErrorMessage
                        name="net_amount_try"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && (
                      <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                        <div className="text-white-100 font-medium text-sm">Çekim Özeti</div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between text-gray-300">
                            <span>Net Alacağınız Tutar:</span>
                            <span>{formatMoney(values.net_amount_try)} TL</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>Komisyon ({profileData?.payout_fee}% + {payoutFeeFixed.value} TL):</span>
                            <span>{formatMoney(commissionInfo.feeAmount)} TL</span>
                          </div>
                          <div className="border-t border-gray-600 pt-1">
                            <div className="flex justify-between text-white-100 font-medium">
                              <span>Hesabınızdan Kesilecek Toplam:</span>
                              <span>{formatMoney(commissionInfo.grossAmount)} TL</span>
                            </div>
                          </div>

                          {/* Auto Payout Indicator */}
                          {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && formatLimitUsage() && (
                            <div className="mt-2">
                              {isAutoPayoutSuitable(values.net_amount_try) ? (
                                <div className="pt-2 mt-3">
                                  <div className="flex items-center text-green-400 text-xs">
                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Otomatik gönderilecek
                                </div>
                                <div className="text-gray-300 text-xs">
                                  (Günlük kalan: <span className="text-green-400 font-medium">{formatLimitUsage().remainingAmount.toLocaleString('tr-TR')} TL</span> / Toplam: <span className="text-orange-500 font-medium">{formatLimitUsage().dailyLimit.toLocaleString('tr-TR')} TL</span>)
                                </div></div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="small"
                      loading={isCreatePayoutLoading}
                      disabled={isCreatePayoutLoading || (values.net_amount_try && finalBalance < 0)}
                      className="bg-primary hover:bg-primary-dark text-white-100 rounded-lg w-full py-2 mt-4"
                    >
                      Talep Oluştur
                    </Button>

                    {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && (
                      <div className="flex flex-col space-y-2 mt-4">
                        <span className={`block text-sm font-medium ${finalBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          İşlem Sonu Bakiyeniz: {formatMoney(finalBalance)} TL
                        </span>
                        {finalBalance < 0 && (
                          <span className="text-red-400 text-xs">
                            Yetersiz bakiye! Çekim için {formatMoney(Math.abs(finalBalance))} TL eksik.
                          </span>
                        )}
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePayoutsContainer;
